import React from 'react';

/**
 * Contenu des mentions légales relatives aux demandes de crédit.
 * Utilisé par la page /conditions-credit et par la page /consentement-conjoint.
 *
 * ⚠️ Ce texte doit rester synchronisé avec la version backend
 * (backend/email_templates/conditions_credit_content.py) qui alimente
 * l'e-mail de consentement du conjoint et l'attestation PDF.
 */
const ConditionsCreditContent = () => (
  <>
    {/* Intro */}
    <div className="text-gray-300 space-y-4 text-sm leading-relaxed mb-10">
      <p>
        Je prends acte du fait que ATOM Group SA (ci-après « l’intermédiaire ») transmet ma demande pour examen à Cembra Money Bank SA et BANK-now SA (ci-après individuellement « l’établissement de financement ») par Internet, par e-mail ou par fax/courrier.
      </p>
      <p>
        En outre, je reconnais et confirme ce qui suit :
      </p>
    </div>

    {/* Section 1 */}
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-blue-400 mb-4">
        Examen de la demande et renseignements sur la solvabilité du demandeur de crédit
      </h2>
      <div className="text-gray-300 space-y-4 text-sm leading-relaxed">
        <p>
          Je confirme que toutes les informations fournies dans ma demande sont complètes, à jour et exactes et j’autorise l’établissement de financement à se procurer des renseignements à mon sujet dans le cadre de l’examen de ma demande et de l’exécution de la relation contractuelle auprès de tiers, notamment auprès de banques, de la centrale d’information de crédit (ZEK), d’autorités (p. ex. l’office des poursuites, l’office du registre foncier, les administrations fiscales, le contrôle des habitants, les autorités de protection de l’enfant et de l’adulte), de sociétés de renseignements économiques, d’intermédiaires de crédit, d’employeurs, de sociétés du groupe de l’établissement de financement et, le cas échéant, du centre de renseignements sur le crédit à la consommation (IKO).
        </p>
        <p>
          Aux fins précitées, je délie ces organismes du secret professionnel du banquier, du secret postal, du secret de fonction ou du secret d’affaires. Je prends acte du fait que l’établissement de financement est tenu, le cas échéant, de déclarer un leasing à la consommation ou un crédit à la consommation à octroyer ou octroyé auprès du centre de renseignements sur le crédit à la consommation (IKO). J’autorise en outre l’établissement de financement à déclarer la présente demande à la ZEK et, en cas d’obligations légales correspondantes, à d’autres organismes.
        </p>
        <p>
          Sont notamment déclarés le type, le montant et les modalités de crédit ou de financement, ainsi que les données personnelles du ou des demandeurs et les éventuels arriérés de paiement qualifiés ou abus. Je reconnais que la ZEK a le droit de communiquer à ses membres des renseignements sur les données transmises. L’établissement de financement peut refuser la demande sans fournir de motifs.
        </p>
      </div>
    </section>

    {/* Section 2 */}
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-blue-400 mb-4">
        Collaboration avec des intermédiaires
      </h2>
      <div className="text-gray-300 space-y-4 text-sm leading-relaxed">
        <p>
          Je prends acte du fait que, dans le cas de demandes transmises par un intermédiaire, l’établissement de financement peut échanger les informations nécessaires avec cet intermédiaire dans le cadre de l’examen de la demande, de la solvabilité ainsi que de la conclusion et de l’exécution du contrat (par exemple les paramètres et le résultat de l’examen de la demande, la remise des documents contractuels, les informations sur le statut du contrat, le comportement de paiement ainsi que le solde). Je délie l’établissement de financement du secret professionnel du banquier dans ce cadre et dans la mesure nécessaire.
        </p>
      </div>
    </section>

    {/* Section 3 */}
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-blue-400 mb-4">
        Informations sur les partenaires
      </h2>
      <div className="text-gray-300 space-y-4 text-sm leading-relaxed">
        <p>
          Si j’ai fourni des informations sur mon conjoint, ma conjointe ou mon partenaire enregistré dans la demande, je confirme que :
        </p>
        <ul className="list-disc list-inside pl-4 space-y-2">
          <li>j’ai informé mon partenaire de cette demande ;</li>
          <li>l’établissement de financement peut vérifier les informations fournies en contactant directement mon partenaire ;</li>
          <li>mon partenaire accepte que l’établissement de financement puisse obtenir des informations le concernant comme décrit ci-dessus, y compris en adressant des demandes d’informations à la ZEK.</li>
        </ul>
      </div>
    </section>

    {/* Section 4 */}
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-blue-400 mb-4">
        Traitement des données
      </h2>
      <div className="text-gray-300 space-y-4 text-sm leading-relaxed">
        <p>
          Je prends acte du fait que l’établissement de financement peut traiter mes données pour examiner ma demande ainsi qu’à des fins d’évaluation des risques et de marketing (évaluation des données et établissement d’un profil).
        </p>
        <p>
          En outre, d’autres produits et services, y compris ceux des sociétés du groupe de l’établissement de financement et d’autres tiers, peuvent m’être proposés par courrier, téléphone ou e-mail. Je peux révoquer à tout moment ce traitement de mes données à des fins de marketing.
        </p>
        <p>
          En indiquant mon adresse e-mail, j’accepte également que l’établissement de financement puisse me faire parvenir les offres susmentionnées par e-mail. Dans le cadre de la communication ouverte par e-mail, j’accepte expressément le risque que mes données puissent être interceptées ou divulguées par des tiers lors de leur transmission, ou que des tiers non autorisés puissent en déduire l’existence d’une relation contractuelle entre l’établissement de financement et moi-même.
        </p>
        <p>
          Je peux révoquer à tout moment mon consentement à l’envoi électronique de messages publicitaires.
        </p>
      </div>
    </section>

    {/* Section 5 */}
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-blue-400 mb-4">
        Collaboration avec les prestataires de services (externalisation)
      </h2>
      <div className="text-gray-300 space-y-4 text-sm leading-relaxed">
        <p>
          Je prends acte du fait que l’établissement de financement peut sous-traiter certains services à des tiers, notamment dans les domaines du traitement des processus commerciaux, de la sécurité informatique, du contrôle des systèmes, des études de marché, du calcul des risques de crédit et de marché ainsi que de l’administration des relations contractuelles.
        </p>
        <p>
          L’établissement de financement fournit à ces prestataires les données nécessaires à l’exécution de leurs obligations contractuelles et peut également transmettre ces données à l’étranger à cette fin. Les prestataires, leurs collaborateurs et leurs sous-traitants sont contractuellement tenus au respect de la confidentialité.
        </p>
        <p>
          Les prestataires sont généralement situés en Suisse ou dans l’Union européenne / l’Espace économique européen, mais peuvent également être établis dans d’autres pays. Si des données personnelles sont transférées dans un pays ne disposant pas d’un niveau de protection adéquat au sens du droit suisse, l’établissement de financement met en place les garanties appropriées, notamment au moyen des clauses contractuelles types reconnues.
        </p>
        <p>
          Je prends acte du fait que la communication entre l’établissement de financement et moi-même ou l’intermédiaire peut se faire par voie électronique. L’intermédiaire et l’établissement de financement ne peuvent pas garantir la confidentialité des données lors de leur transmission par Internet. Des tiers peuvent donc en déduire l’existence ou l’existence imminente d’une relation d’affaires, voire d’une relation bancaire, entre moi et l’établissement de financement.
        </p>
        <p>
          Pour plus d’informations sur le traitement des données personnelles, veuillez consulter la déclaration de protection des données disponible sur le site internet de l’établissement de financement ou vous renseigner directement auprès de celui-ci.
        </p>
      </div>
    </section>

    {/* Mention légale finale */}
    <div className="text-gray-200 text-sm leading-relaxed font-medium border-l-4 border-blue-400 pl-4 py-2 bg-white/5 rounded-r-lg">
      L’octroi d’un crédit est interdit s’il occasionne le surendettement du consommateur (art. 3 LCD).
    </div>
  </>
);

export default ConditionsCreditContent;
